import React, { useState, useRef, useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@components/shadcn-ui/button";
import { Card } from "@components/shadcn-ui/card";
import { DynamicFormField, FieldType } from "@components/ui/DynamicInput";
import { useToast } from "@customHooks/useToast";
import { Loader2, Camera, X, ChevronLeft } from "lucide-react";
import { getPresignedUrl, uploadFileToPresignedUrl, getPresignedUrlFromApplicationId } from "../../features/createApplication/service/uploadFileService";
import { applicationService } from "../../features/createApplication/service/applicationService";
import { useGeolocation } from "@customHooks/useGeolocation";
import { INDIAN_STATES,CITY_MAP } from "features/loanApplications/formFieldsConfig/indiaStatesList";
import {isFieldAsCity, encodeURIComponent } from "@lib/utils";
import {
  completeResidenceVerification,
  draftResidenceVerification,
  extractRvErrorCode,
  generateIdempotencyKey,
  getResidenceVerification,
  RvPayload,
} from "../../v2/features/residence-verification/services/rv-service";
import { parseThirdPartyReference } from "../../v2/features/residence-verification/services/rv-flow";



interface ResidenceVerificationData {
  addressIsSameAsKYC: string;
  occupancyStatus: string;
  applicantStayStatus: string;
  noOfOccupant: string;
  noOfEarningMember: string;
  noOfMembersDependentOnApplicant: string;
  noOfApplicantChildrenStudying: string;
  houseWithKitchen: string;
  electricityBillNo?: string;
  electricityBillAmount?: string;
  electricityBillOverdueAmount?: string;
  monthlyResidentialExpenses?: string;
  houseAccessibility: string;
  noOfRooms: string;
  gatedHouse: string;
  communityDominatedArea: string;
  spouseName: string;
  spouseAwareOfLoan: string;
  appliedLoanAmount: string;
  thirdPartyReference: string;
  standardOfLiving: string;
  agentRemarks: string;
  addressType: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  ownership: string;
  monthlyRent: string;
  yearsOfStay: string;
}

interface ResidenceVerificationPageProps {
  applicationId?: string;
  inlineMode?: boolean;
  onCompleteSuccess?: () => void;
  onMoveToNextSection?: () => void;
}

const FORM_SECTIONS = {
  address: [
    { name: "addressIsSameAsKYC", label: "Address is same as KYC", type: "select" as const, options: ["Yes", "No"], validationRules: { required: "This field is required" } },
    { name: "addressType", label: "Address Type", type: "select" as const, options: ["PERMANENT", "CURRENT"], validationRules: { required: "This field is required" } },
    { name: "addressLine1", label: "Address Line 1", type: "text" as const, validationRules: { required: "This field is required" } },
    { name: "addressLine2", label: "Address Line 2", type: "text" as const, validationRules: {} },
    { name: "city", label: "City", type: "select" as const, options: [], validationRules: { required: "This field is required" } },
    { name: "state", label: "State", type: "select" as const, options: INDIAN_STATES, validationRules: { required: "This field is required" } },
    { name: "country", label: "Country", type: "text" as const, validationRules: { required: "This field is required" } },
    { name: "postalCode", label: "Postal Code", type: "text" as const, validationRules: { required: "This field is required", pattern: { value: /^\d{5,6}$/, message: "Enter valid postal code" } } }
  ],
  occupancy: [
    { name: "ownership", label: "Ownership", type: "select" as const, options: ["OWNED", "RENTED"], validationRules: { required: "This field is required" } },
    { name: "occupancyStatus", label: "Occupancy Status", type: "select" as const, options: ["Owned", "Rented", "Bachelor", "Parental", "Joint Family"], validationRules: { required: "This field is required" } },
    { name: "applicantStayStatus", label: "Applicant Stay Status", type: "select" as const, options: ["Permanent", "Temporary", "Does not stay"], validationRules: { required: "This field is required" } },
    { name: "monthlyRent", label: "Monthly Rent", type: "text" as const, validationRules: { required: "This field is required", pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" } } },
    { name: "yearsOfStay", label: "Years of Stay", type: "text" as const, validationRules: { required: "This field is required", pattern: { value: /^\d+$/, message: "Enter valid number" } } }
  ],
  household: [
    { name: "noOfOccupant", label: "No of Occupant", type: "select" as const, options: Array.from({ length: 10 }, (_, i) => (i + 1).toString()), validationRules: { required: "This field is required" } },
    { name: "noOfEarningMember", label: "No of Earning Member", type: "select" as const, options: Array.from({ length: 11 }, (_, i) => i.toString()), validationRules: { required: "This field is required" } },
    { name: "noOfMembersDependentOnApplicant", label: "No of Members Dependent on Applicant", type: "select" as const, options: Array.from({ length: 6 }, (_, i) => i.toString()), validationRules: { required: "This field is required" } },
    { name: "noOfApplicantChildrenStudying", label: "No of Applicant's Children Studying", type: "select" as const, options: Array.from({ length: 5 }, (_, i) => i.toString()), validationRules: { required: "This field is required" } }
  ],
  property: [
    { name: "noOfRooms", label: "No of Rooms", type: "select" as const, options: Array.from({ length: 10 }, (_, i) => (i + 1).toString()), validationRules: { required: "This field is required" } },
    { name: "houseWithKitchen", label: "House with Kitchen", type: "select" as const, options: ["Yes", "No"], validationRules: { required: "This field is required" } },
    { name: "houseAccessibility", label: "House Accessibility", type: "select" as const, options: ["No road", "2W road", "4W road"], validationRules: { required: "This field is required" } },
    { name: "gatedHouse", label: "Gated House", type: "select" as const, options: ["Yes", "No"], validationRules: { required: "This field is required" } },
    { name: "communityDominatedArea", label: "Community Dominated Area", type: "select" as const, options: ["Yes", "No"], validationRules: { required: "This field is required" } }
  ],
  financial: [
    { name: "electricityBillNo", label: "Electricity Bill No", type: "text" as const, validationRules: {} },
    { name: "electricityBillAmount", label: "Electricity Bill Amount", type: "text" as const, validationRules: {pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" } } },
    { name: "electricityBillOverdueAmount", label: "Electricity Bill Overdue Amount", type: "text" as const, validationRules: {  pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" } } },
    { name: "monthlyResidentialExpenses", label: "Monthly Residential Expenses", type: "text" as const, validationRules: { pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" } } },
    { name: "appliedLoanAmount", label: "Applied Loan Amount", type: "text" as const, validationRules: { required: "This field is required", pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" } } }
  ],
  spouse: [
    { name: "spouseName", label: "Spouse Name", type: "text" as const, validationRules: {} },
    { name: "spouseAwareOfLoan", label: "Spouse Aware of Loan", type: "select" as const, options: ["Yes", "No"], validationRules: { required: "This field is required" } }
  ],
  verification: [
    { name: "thirdPartyReference", label: "Third Party Reference", type: "select" as const, options: ["Negative", "Positive"], validationRules: { required: "This field is required" } },
    { name: "standardOfLiving", label: "Standard of Living", type: "select" as const, options: ["High", "Medium", "Low", "Poor"], validationRules: { required: "This field is required" } },
    { name: "agentRemarks", label: "Agent Remarks", type: "textarea" as const, validationRules: {} }
  ]
};

export const ResidenceVerificationPage: React.FC<ResidenceVerificationPageProps> = ({
  applicationId: applicationIdProp,
  inlineMode = false,
  onCompleteSuccess,
  onMoveToNextSection,
}) => {
  const { applicationId: routeApplicationId } = useParams<{ applicationId: string }>();
  const applicationId = applicationIdProp || routeApplicationId;
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitMode, setSubmitMode] = useState<"draft" | "complete" | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhotos, setCapturedPhotos] = useState<string[]>([]);
  const [applicationData, setApplicationData] = useState<any>(null);
  const [isApplicationLoading, setIsApplicationLoading] = useState(true);
  const [applicationLoadError, setApplicationLoadError] = useState<string | null>(null);
  const [isCompletedInline, setIsCompletedInline] = useState(false);
  // Photos already persisted in S3 from a previous draft save.
  // Each entry has { fileName, presignedUrl } for display.
  const [persistedPhotos, setPersistedPhotos] = useState<{ fileName: string; presignedUrl: string }[]>([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  // Skip the city-reset useEffect when hydrating form from aggregate
  const isHydratingRef = useRef(false);
  // Preserve the idempotency key across retries so AMS can dedupe a duplicate
  // /complete submission (e.g. user retries after a network blip).
  const idempotencyKeyRef = useRef<string | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  const { userLocation, locationError, isLocationAllowed } = useGeolocation();
  const isSubmitting = submitMode !== null;

  const form = useForm<ResidenceVerificationData>({
    mode: "all",
    defaultValues: {
      addressType: "PERMANENT",
      country: "India",
      ownership: "OWNED",
      monthlyRent: "0",
      yearsOfStay: "1"
    }
  });

  const hydrateFormFromAggregate = useCallback((aggregate: any) => {
    if (!aggregate) {
      return;
    }

    isHydratingRef.current = true;

    // stayStatus is stored uppercase (e.g. "BACHELOR") but the form select
    // options are title-case (e.g. "Bachelor"). Convert back for the dropdown.
    const rawStayStatus = aggregate.stayStatus || "";
    const titleCaseStayStatus = rawStayStatus
      ? rawStayStatus.charAt(0).toUpperCase() + rawStayStatus.slice(1).toLowerCase()
      : "";
    // Map "Joint family" back to the exact option label "Joint Family"
    const stayStatusMap: Record<string, string> = {
      "Owned": "Owned",
      "Rented": "Rented",
      "Bachelor": "Bachelor",
      "Parental": "Parental",
      "Joint family": "Joint Family",
      "Does not stay": "Does not stay",
    };
    const occupancyStatus = stayStatusMap[titleCaseStayStatus] || titleCaseStayStatus;

    form.reset({
      addressIsSameAsKYC:
        aggregate.additionalResidentialDetails?.addressIsSameAsKYC == null
          ? "No"
          : aggregate.additionalResidentialDetails.addressIsSameAsKYC
            ? "Yes"
            : "No",
      occupancyStatus,
      applicantStayStatus:
        aggregate.additionalResidentialDetails?.applicantStayStatus || "",
      noOfOccupant:
        aggregate.additionalResidentialDetails?.occupantsCount != null
          ? String(aggregate.additionalResidentialDetails.occupantsCount)
          : "",
      noOfEarningMember:
        aggregate.additionalResidentialDetails?.earningMemberCount != null
          ? String(aggregate.additionalResidentialDetails.earningMemberCount)
          : "",
      noOfMembersDependentOnApplicant:
        aggregate.additionalResidentialDetails?.dependentMemberCount != null
          ? String(aggregate.additionalResidentialDetails.dependentMemberCount)
          : "",
      noOfApplicantChildrenStudying:
        aggregate.additionalResidentialDetails?.childrenStudyingCount != null
          ? String(aggregate.additionalResidentialDetails.childrenStudyingCount)
          : "",
      houseWithKitchen:
        aggregate.additionalResidentialDetails?.kitchen == null
          ? ""
          : aggregate.additionalResidentialDetails.kitchen
            ? "Yes"
            : "No",
      electricityBillNo: aggregate.additionalResidentialDetails?.electricityBillNo || "",
      electricityBillAmount:
        aggregate.additionalResidentialDetails?.electricityBillAmount != null
          ? String(aggregate.additionalResidentialDetails.electricityBillAmount)
          : "",
      electricityBillOverdueAmount:
        aggregate.additionalResidentialDetails?.overdueElectricityBillAmount != null
          ? String(aggregate.additionalResidentialDetails.overdueElectricityBillAmount)
          : "",
      monthlyResidentialExpenses:
        aggregate.additionalResidentialDetails?.monthlyResidentialExpenses != null
          ? String(aggregate.additionalResidentialDetails.monthlyResidentialExpenses)
          : "",
      houseAccessibility: aggregate.additionalResidentialDetails?.accessibility || "",
      noOfRooms:
        aggregate.additionalResidentialDetails?.roomsCount != null
          ? String(aggregate.additionalResidentialDetails.roomsCount)
          : "",
      gatedHouse:
        aggregate.additionalResidentialDetails?.gatedHouse == null
          ? ""
          : aggregate.additionalResidentialDetails.gatedHouse
            ? "Yes"
            : "No",
      communityDominatedArea:
        aggregate.additionalResidentialDetails?.communityDominatedArea == null
          ? ""
          : aggregate.additionalResidentialDetails.communityDominatedArea
            ? "Yes"
            : "No",
      spouseName: aggregate.additionalResidentialDetails?.spouseName || "",
      spouseAwareOfLoan:
        aggregate.additionalResidentialDetails?.spouseAwareOfLoan == null
          ? ""
          : aggregate.additionalResidentialDetails.spouseAwareOfLoan
            ? "Yes"
            : "No",
      appliedLoanAmount:
        aggregate.additionalResidentialDetails?.appliedLoanAmount != null
          ? String(aggregate.additionalResidentialDetails.appliedLoanAmount)
          : "",
      thirdPartyReference:
        aggregate.thirdPartyReference == null
          ? ""
          : aggregate.thirdPartyReference
            ? "Positive"
            : "Negative",
      standardOfLiving: aggregate.additionalResidentialDetails?.livingStandard || "",
      agentRemarks: aggregate.remarks || "",
      addressType: aggregate.address?.type || "PERMANENT",
      addressLine1: aggregate.address?.line1 || "",
      addressLine2: aggregate.address?.line2 || "",
      city: aggregate.address?.city || "",
      state: aggregate.address?.state || "",
      country: aggregate.address?.country || "India",
      postalCode:
        aggregate.address?.postalCode != null ? String(aggregate.address.postalCode) : "",
      ownership: aggregate.address?.ownership || "OWNED",
      monthlyRent:
        aggregate.address?.monthlyRent != null ? String(aggregate.address.monthlyRent) : "0",
      yearsOfStay:
        aggregate.address?.yearsOfStay != null ? String(aggregate.address.yearsOfStay) : "1",
    });

    // Allow the city-reset useEffect to run normally after hydration settles
    setTimeout(() => {
      isHydratingRef.current = false;
      // Trigger validation so the submit button enables when all fields are pre-filled
      form.trigger();
    }, 0);
  }, [form]);

  const buildPayload = async (data: ResidenceVerificationData, options?: { uploadDocuments?: boolean }) => {
    let fileNames: string[] = [];

    // Start with already-persisted filenames so they aren't lost
    const existingFileNames = persistedPhotos.map((p) => p.fileName);

    // Only upload newly captured photos (data URLs), not the already-persisted ones
    if (options?.uploadDocuments && capturedPhotos.length > 0) {
      const newFileNames = await uploadPhotos(capturedPhotos);
      fileNames = [...existingFileNames, ...newFileNames];
    } else if (existingFileNames.length > 0) {
      fileNames = existingFileNames;
    }

    const payload: RvPayload = {
      address: {
        type: data.addressType,
        line1: data.addressLine1,
        line2: data.addressLine2,
        city: data.city,
        state: data.state,
        country: data.country,
        latitude: userLocation?.latitude || 0,
        longitude: userLocation?.longitude || 0,
        postalCode: data.postalCode ? parseInt(data.postalCode) : undefined,
        ownership: data.ownership,
        monthlyRent: data.monthlyRent ? parseFloat(data.monthlyRent) : undefined,
        yearsOfStay: data.yearsOfStay ? parseInt(data.yearsOfStay) : undefined,
        active: true,
        verified: false
      },
      additionalResidentialDetails: {
        spouseName: data.spouseName || '',
        spouseAwareOfLoan: data.spouseAwareOfLoan ? data.spouseAwareOfLoan === 'Yes' : undefined,
        livingStandard: data.standardOfLiving || undefined,
        accessibility: data.houseAccessibility || undefined,
        electricityBillNo: data.electricityBillNo || undefined,
        electricityBillAmount: data.electricityBillAmount
          ? parseFloat(data.electricityBillAmount)
          : undefined,
        overdueElectricityBillAmount: data.electricityBillOverdueAmount
          ? parseFloat(data.electricityBillOverdueAmount)
          : undefined,
        gatedHouse: data.gatedHouse ? data.gatedHouse === 'Yes' : undefined,
        kitchen: data.houseWithKitchen ? data.houseWithKitchen === 'Yes' : undefined,
        communityDominatedArea: data.communityDominatedArea
          ? data.communityDominatedArea === 'Yes'
          : undefined,
        earningMemberCount: data.noOfEarningMember ? parseInt(data.noOfEarningMember) : undefined,
        occupantsCount: data.noOfOccupant ? parseInt(data.noOfOccupant) : undefined,
        childrenStudyingCount: data.noOfApplicantChildrenStudying
          ? parseInt(data.noOfApplicantChildrenStudying)
          : undefined,
        roomsCount: data.noOfRooms ? parseInt(data.noOfRooms) : undefined,
        dependentMemberCount: data.noOfMembersDependentOnApplicant
          ? parseInt(data.noOfMembersDependentOnApplicant)
          : undefined,
        monthlyResidentialExpenses: data.monthlyResidentialExpenses
          ? parseFloat(data.monthlyResidentialExpenses)
          : undefined,
        appliedLoanAmount: data.appliedLoanAmount
          ? parseFloat(data.appliedLoanAmount)
          : undefined,
        applicantStayStatus: data.applicantStayStatus || undefined,
        addressIsSameAsKYC: data.addressIsSameAsKYC
          ? data.addressIsSameAsKYC === 'Yes'
          : undefined,
      },
      stayStatus: data.occupancyStatus ? data.occupancyStatus.toUpperCase() : undefined,
      thirdPartyReference: parseThirdPartyReference(data.thirdPartyReference),
      remarks: data.agentRemarks || undefined,
      documents: fileNames.length > 0 ? fileNames : undefined
    };

    return payload;
  };

  React.useEffect(() => {
    const fetchApplicationData = async () => {
      if (applicationId) {
        setIsApplicationLoading(true);
        setApplicationLoadError(null);
        try {
          const data = await applicationService.getApplicationById(applicationId);
          setApplicationData(data);
          const aggregateResponse = await getResidenceVerification(applicationId);
          hydrateFormFromAggregate(aggregateResponse?.aggregate);

          // Load previously uploaded photos from S3 so the user sees them
          if (aggregateResponse?.aggregate?.documents?.length) {
            setIsLoadingPhotos(true);
            try {
              const downloadResponse = await getPresignedUrlFromApplicationId(applicationId);
              const rvDocs = (downloadResponse.documents?.["RESIDENCE_VERIFICATION_FORM"] || [])
                .filter((doc: any) =>
                  doc.objectKey.toLowerCase().endsWith('.jpg') ||
                  doc.objectKey.toLowerCase().endsWith('.jpeg') ||
                  doc.objectKey.toLowerCase().endsWith('.png')
                )
                .map((doc: any) => ({
                  fileName: doc.objectKey.split('/').pop(),
                  presignedUrl: doc.presignedUrl,
                }));
              setPersistedPhotos(rvDocs);
            } catch (photoError) {
              console.error("Failed to load existing RV photos:", photoError);
            } finally {
              setIsLoadingPhotos(false);
            }
          }
        } catch (error) {
          setApplicationLoadError("Failed to load application details.");
          console.error("Error fetching application data:", error);
        } finally {
          setIsApplicationLoading(false);
        }
      } else {
        setIsApplicationLoading(false);
      }
    };
    fetchApplicationData();
  }, [applicationId, hydrateFormFromAggregate]);
  
  const selectedState = form.watch("state");
  useEffect(() => {
    // Don't wipe city when the state change comes from hydrating a saved aggregate
    if (isHydratingRef.current) return;
    form.setValue("city", "");
  }, [form, selectedState]);


  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      setShowCamera(true);
      
      // Wait for the modal to render before setting up video
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
          
          // Ensure video plays when metadata is loaded
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play();
          };
        }
      }, 100);
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Failed to access camera. Please check permissions."
      });
    }
  }, [toast]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  }, []);

  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setCapturedPhotos(prev => [...prev, photoDataUrl]);
        stopCamera();
      }
    }
  }, [stopCamera]);

  const removePhoto = useCallback((index: number) => {
    setCapturedPhotos(prev => prev.filter((_, i) => i !== index));
  }, []);

  const uploadPhotos = async (photos: string[]): Promise<string[]> => {
    if (!applicationId)
      throw new Error("Application Id cannot be null or undefined!");

    const fileNames = [];
    
    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i];
      const response = await fetch(photo);
      const blob = await response.blob();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `residence-photo-${i + 1}-${timestamp}.jpg`;
      const file = new File([blob], fileName, { type: 'image/jpeg' });

      const presignedResponse = await getPresignedUrl({
        filename: file.name,
        documentType: "RESIDENCE_VERIFICATION_FORM",
        applicationId
      });
      
      await uploadFileToPresignedUrl(file, presignedResponse.fileUploadUrl, () => {});

      fileNames.push(fileName)
    }

    return fileNames;
  };

  const onSaveDraft = async () => {
    if (!applicationId) {
      toast({
        variant: "destructive",
        description: "Application ID is required"
      });
      return;
    }

    setSubmitMode("draft");
    try {
      const payload = await buildPayload(form.getValues(), {
        uploadDocuments: capturedPhotos.length > 0
      });
      await draftResidenceVerification(applicationId, payload);
      toast({
        description: "Residence verification draft saved successfully"
      });
    } catch (error) {
      const rvCode = extractRvErrorCode(error);
      const description = rvCode === 'ADDRESS_PREREQUISITE_NOT_MET'
        ? 'Please save the application address before saving verification.'
        : rvCode === 'RESIDENCE_VERIFICATION_POLICY_DENIED'
          ? 'Your role is not allowed to save residence verification.'
          : rvCode === 'RESIDENCE_VERIFICATION_ALREADY_COMPLETED'
            ? 'Residence verification is already completed for this application.'
            : rvCode === 'RESIDENCE_VERIFICATION_VERSION_CONFLICT'
              ? 'This verification was updated elsewhere — please refresh and try again.'
              : 'Failed to save residence verification draft';

      toast({
        variant: "destructive",
        description
      });
    } finally {
      setSubmitMode(null);
    }
  };

  const onSubmit = async (data: ResidenceVerificationData) => {
    if (!applicationId) {
      toast({
        variant: "destructive",
        description: "Application ID is required"
      });
      return;
    }

    if (!data.addressLine1 || !data.city || !data.state) {
      toast({
        variant: "destructive",
        description: "Please add address details"
      });
      return;
    }

    if ((persistedPhotos.length + capturedPhotos.length) < 5) {
      toast({
        variant: "destructive",
        description: "Please capture at least five photos"
      });
      return;
    }

    if (isApplicationLoading || !applicationData) {
      toast({
        variant: "destructive",
        description: applicationLoadError ?? "Application details are not ready yet."
      });
      return;
    }

    setSubmitMode("complete");
    try {
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = generateIdempotencyKey();
      }
      const payload = await buildPayload(data, {
        uploadDocuments: true
      });
      await completeResidenceVerification(applicationId, payload, {
        idempotencyKey: idempotencyKeyRef.current
      });

      // Reset key on success so a fresh submission starts a new logical op.
      idempotencyKeyRef.current = null;

      toast({
        description: "Residence verification submitted successfully"
      });

      if (inlineMode) {
        setIsCompletedInline(true);
        onCompleteSuccess?.();
        return;
      }
      navigate(`/dashboard/residence-verification/view/${applicationId}?submitted=true`);
    } catch (error) {
      const rvCode = extractRvErrorCode(error);
      const description = rvCode === 'ADDRESS_PREREQUISITE_NOT_MET'
        ? 'Please save the application address before submitting verification.'
        : rvCode === 'RESIDENCE_VERIFICATION_INCOMPLETE'
          ? 'Required verification fields are missing; please complete the form.'
          : rvCode === 'RESIDENCE_VERIFICATION_POLICY_DENIED'
            ? 'Your role is not allowed to complete residence verification.'
            : rvCode === 'RESIDENCE_VERIFICATION_PRODUCT_NOT_ENABLED'
              ? 'Residence verification is not available for this product.'
              : rvCode === 'RESIDENCE_VERIFICATION_ALREADY_COMPLETED'
                ? 'Residence verification is already completed for this application.'
              : rvCode === 'RESIDENCE_VERIFICATION_VERSION_CONFLICT'
                ? 'This verification was updated elsewhere — please refresh and try again.'
                : 'Failed to submit residence verification';

      toast({
        variant: "destructive",
        description
      });
    } finally {
      setSubmitMode(null);
    }
  };

  if (locationError || !isLocationAllowed) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="p-6 max-w-md">
          <div className="text-center space-y-4">
            <h2 className="text-xl font-bold text-red-600">Location Access Required</h2>
            <p className="text-gray-600">{locationError || "Location permission is required to proceed with residence verification."}</p>
            <Button onClick={() => window.location.reload()} className="w-full">
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

 
  return (
    <div>
      <Card className="p-6">
        <div className="mb-6">
          {!inlineMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/dashboard/application/details/${encodeURIComponent(applicationId!)}`)
              }
              className="flex items-center gap-2 mb-4"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="sm:inline">Application</span>
            </Button>
          )}
          <h2 className="text-2xl font-bold">Residence Verification</h2>
        </div>
        <FormProvider {...form}>
          <div className="space-y-6">
            {applicationLoadError && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {applicationLoadError}
              </div>
            )}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Address Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.address.map((field, index) => { 
                  const dynamicOptions = isFieldAsCity(field.name) ? CITY_MAP[selectedState] || []  : field.options || []; 

                  return(
                    <div key={index} className="w-full">
                      <DynamicFormField field={{...field, options : dynamicOptions} as FieldType} isDisabled={isSubmitting || (isFieldAsCity(field.name) && !selectedState)} />
                    </div>
                    
                  );  
              })}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Occupancy Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.occupancy.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Household Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.household.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Property Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.property.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Financial Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.financial.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Spouse Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.spouse.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Verification Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.verification.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Capture Photos ({persistedPhotos.length + capturedPhotos.length})</h3>
              
              {isLoadingPhotos && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading previously uploaded photos...
                </div>
              )}

              {(
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Previously uploaded photos */}
                  {persistedPhotos.map((photo, index) => (
                    <div key={`persisted-${index}`} className="relative">
                      <img src={photo.presignedUrl} alt={`Uploaded photo ${index + 1}`} className="w-full h-48 object-cover rounded border" />
                      <span className="absolute top-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded">
                        Uploaded
                      </span>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setPersistedPhotos(prev => prev.filter((_, i) => i !== index))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {/* Newly captured photos */}
                  {capturedPhotos.map((photo, index) => (
                    <div key={`new-${index}`} className="relative">
                      <img src={photo} alt={`New photo ${index + 1}`} className="w-full h-48 object-cover rounded border" />
                      <span className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                        New
                      </span>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => removePhoto(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {(
                <Button
                  type="button"
                  variant="outline"
                  onClick={startCamera}
                  className="flex items-center gap-2"
                >
                  <Camera className="h-4 w-4" />
                  Capture Photo ({persistedPhotos.length + capturedPhotos.length})
                </Button>
              )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting || isApplicationLoading || !applicationData}
                onClick={onSaveDraft}
                className="w-full"
              >
                {submitMode === "draft" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Draft
              </Button>
              <Button
                type="button"
                disabled={
                  isSubmitting ||
                  isApplicationLoading ||
                  !applicationData ||
                  !form.formState.isValid ||
                  (persistedPhotos.length + capturedPhotos.length) < 5 ||
                  !userLocation
                }
                onClick={() => void form.handleSubmit(onSubmit)()}
                className="w-full"
              >
                {submitMode === "complete" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit Residence Verification
              </Button>
              {inlineMode && isCompletedInline && onMoveToNextSection && (
                <Button type="button" variant="secondary" onClick={onMoveToNextSection} className="w-full">
                  Go to next section
                </Button>
              )}
            </div>
          </div>
        </FormProvider>
      </Card>

      {showCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 sm:p-6 rounded-lg w-full mx-2 sm:mx-4 max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Capture Photo</h2>
              <Button variant="ghost" size="sm" onClick={stopCamera}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-64 bg-black rounded"
                style={{ objectFit: 'cover' }}
              />
              <div className="flex gap-2 justify-center">
                <Button onClick={capturePhoto} disabled={false}>
                  <Camera className="mr-2 h-4 w-4" />
                  Capture
                </Button>
                <Button variant="outline" onClick={stopCamera}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};